/* eslint-disable import/no-internal-modules */
import { useMemo } from 'react';
import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from 'react-query';
import { AuthenticationType } from 'api/models';
import {
  CreateSsoPayload,
  IdpHttpClientApi,
  LoginPolicyPayload,
  SamlConfig,
  SamlMeta,
  UpdateSsoPayload,
} from 'api/security/idp';

const SamlQueryKey = 'saml';

const getSamlQueryMetaKey = (idpId) => ['saml', 'meta', idpId];
const LoginPolicyQuery = 'loginPolicy';

export const useQueryIdps = (): UseQueryResult<SamlConfig[], unknown> =>
  useQuery<Array<SamlConfig>, unknown>(
    SamlQueryKey,
    IdpHttpClientApi.getAllIdp,
    {
      retry: false,
    },
  );

export const useQuerySamlMeta = (
  idpId?: string,
  onSuccess?: (data: SamlMeta) => void,
): UseQueryResult<SamlMeta, unknown> =>
  useQuery(
    getSamlQueryMetaKey(idpId),
    () => IdpHttpClientApi.getSamlMeta(idpId as string),
    {
      enabled: !!idpId,
      onSuccess,
      retry: false,
    },
  );

export const useSSOCreation = (): UseMutationResult<
  SamlConfig,
  unknown,
  CreateSsoPayload
> => {
  const queryClient = useQueryClient();

  return useMutation(IdpHttpClientApi.createIdpWithSSO, {
    onSuccess: () => queryClient.invalidateQueries(SamlQueryKey),
  });
};

export const useSSOUpdate = (): UseMutationResult<
  null,
  unknown,
  UpdateSsoPayload
> => {
  const queryClient = useQueryClient();

  return useMutation(IdpHttpClientApi.updateIdpWithSSO, {
    onSuccess: () => queryClient.invalidateQueries(SamlQueryKey),
  });
};

export const useSamlDelete = (): UseMutationResult<
  unknown,
  unknown,
  string
> => {
  const queryClient = useQueryClient();

  return useMutation(IdpHttpClientApi.deleteIdp, {
    onSuccess: () => queryClient.invalidateQueries(SamlQueryKey),
  });
};

export const useGetSamlById = (
  id?: string,
): {
  data?: SamlConfig;
  isLoading: boolean;
} => {
  const { data, isLoading } = useQueryIdps();

  return useMemo(() => {
    return { data: data?.find(({ idpId }) => idpId === id), isLoading };
  }, [data, id, isLoading]);
};

export const useLoginPolicy = (): UseQueryResult<
  Array<AuthenticationType>,
  unknown
> => {
  return useQuery(LoginPolicyQuery, IdpHttpClientApi.getLoginPolicy);
};

export const useMutateLoginPolicy = (): UseMutationResult<
  Array<AuthenticationType>,
  unknown,
  Array<LoginPolicyPayload>,
  { previousPolicy?: Array<AuthenticationType> }
> => {
  const queryClient = useQueryClient();

  return useMutation(IdpHttpClientApi.updateLoginPolicy, {
    onSuccess: () => {
      queryClient.invalidateQueries(LoginPolicyQuery);
    },
    onMutate: (newPolicy) => {
      const previousPolicy: Array<AuthenticationType> | undefined =
        queryClient.getQueryData(LoginPolicyQuery);

      queryClient.setQueryData(LoginPolicyQuery, newPolicy);

      return { previousPolicy };
    },
    onError: (err, newPolicy, context) => {
      queryClient.setQueryData(LoginPolicyQuery, context?.previousPolicy);
    },
  });
};
